import React, {useState, useEffect, useCallback} from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  Pagination,
  Alert,
  Snackbar,
  Menu,
  IconButton,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import {MoreHorizontal, Trash2} from 'lucide-react';
import SearchGreyIcon from '../../assets/search-grey.svg';
import {SettingsCard} from '../../components/SettingsCard';
import {useAuthFrontendApis} from '@propelauth/frontend-apis-react';
import {useAuthInfo} from '@propelauth/react';
import Loading from '../../components/Loading';
import CompanyPendingInvitations from './CompanyPendingInvitations';
import PropTypes from 'prop-types';

const styles = {
  cardTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '24px',
    color: '#162523',
  },
  searchContainer: {
    marginBottom: '24px',
  },
  searchField: {
    display: 'flex',
    width: '250px',
    padding: '8px 12px',
    alignItems: 'center',
    gap: '12px',
    borderRadius: '8px',
    border: '1px solid #E1E5E4',
    backgroundColor: '#FFF',
  },
  searchInput: {
    border: 'none',
    outline: 'none',
    width: '100%',
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '140%',
    letterSpacing: '-0.14px',
    '&::placeholder': {
      color: '#9AA7A6',
    },
  },
  tableContainer: {
    boxShadow: 'none',
    '& .MuiTable-root': {
      borderCollapse: 'separate',
    },
  },
  tableHeader: {
    backgroundColor: 'transparent',
    '& .MuiTableCell-head': {
      color: '#819190',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '150%',
      letterSpacing: '-0.14px',
      borderBottom: '1px solid #E1E5E4',
      padding: '16px 24px',
      backgroundColor: 'transparent',
    },
  },
  tableRow: {
    backgroundColor: 'transparent !important',
    '&:nth-of-type(odd)': {
      backgroundColor: 'transparent !important',
    },
    '&:nth-of-type(even)': {
      backgroundColor: 'transparent !important',
    },
    '& .MuiTableCell-root': {
      borderBottom: '1px solid #E1E5E4',
      padding: '16px 24px',
      backgroundColor: 'transparent !important',
    },
    '&:last-child .MuiTableCell-root': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
  },
  userCell: {
    display: 'flex',
    padding: '4px 0',
    flexDirection: 'column',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
  },
  userName: {
    color: '#000',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '130%',
    letterSpacing: '-0.14px',
    textTransform: 'capitalize',
  },
  userEmail: {
    color: '#819190',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '140%',
    letterSpacing: '-0.12px',
  },
  statusText: {
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '130%',
    letterSpacing: '-0.14px',
  },
  roleSelect: {
    width: '236px',
    '& .MuiOutlinedInput-root': {
      height: '42px',
      borderRadius: '8px',
      border: '1px solid #E1E5E4',
      width: '236px',
      '& fieldset': {
        border: 'none',
      },
      '&:hover': {
        backgroundColor: '#F2F4F4',
        '& fieldset': {
          border: 'none',
        },
      },
      '&.Mui-focused': {
        backgroundColor: '#F2F4F4',
        '& fieldset': {
          border: 'none',
        },
      },
    },
    '& .MuiSelect-select': {
      color: '#000',
      padding: '8px 16px',
      height: '42px',
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
      minHeight: 'unset',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '&.Mui-focused': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiInputBase-input': {
      padding: '8px 16px',
      height: '26px',
      boxSizing: 'border-box',
    },
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '16px',
    '& .MuiPagination-ul': {
      '& .MuiPaginationItem-root': {
        color: '#819190',
        '&.Mui-selected': {
          backgroundColor: '#26A993',
          color: '#fff',
          '&:hover': {
            backgroundColor: '#26A993',
          },
        },
        '&:hover': {
          backgroundColor: '#f5f5f5',
        },
      },
    },
  },
};

// Custom hook for fetching org members
const useOrgMembers = (orgId) => {
  const {fetchOrgMembers: fetchOrgMembersApi} = useAuthFrontendApis();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchOrgMembers = useCallback(
    async (page = 0, pageSize = 10, search = '') => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchOrgMembersApi(orgId, {
          page_number: page,
          page_size: pageSize,
          email_search: search,
        });

        await response.handle({
          success: (responseData) => {
            setData(responseData);
          },
          orgNotFound: () => {
            setError('Organization not found');
          },
          orgsNotEnabled: () => {
            setError('Organizations are not enabled');
          },
          unauthorized: () => {
            setError('You are not authorized to view this organization');
          },
          unexpectedOrUnhandled: (err) => {
            setError(err.user_facing_error || 'An unexpected error occurred');
          },
        });
      } catch (err) {
        setError('Failed to fetch organization members');
      } finally {
        setIsLoading(false);
      }
    },
    [orgId, fetchOrgMembersApi]
  );

  return {
    isLoading,
    error,
    data,
    fetchOrgMembers,
  };
};

// Role select component
const RoleSelect = ({orgId, orgMember, onRoleUpdate, onShowSnackbar}) => {
  const {updateUserRoleInOrg} = useAuthFrontendApis();
  const {userClass} = useAuthInfo();
  const [isUpdating, setIsUpdating] = useState(false);

  const isCurrentUser = orgMember.user_id === userClass?.userId;

  const handleRoleChange = async (newRole) => {
    if (newRole === orgMember.role) return;

    setIsUpdating(true);
    try {
      const response = await updateUserRoleInOrg({
        org_id: orgId,
        user_id: orgMember.user_id,
        role: newRole,
      });

      await response.handle({
        success: () => {
          onRoleUpdate();
          onShowSnackbar({
            message: `${orgMember.email} role successfully updated`,
            severity: 'success',
          });
        },
        noUpdateRolePermission: () => {
          onShowSnackbar({
            message: "You do not have permission to update this user's role",
            severity: 'error',
          });
        },
        userNotFoundInOrg: () => {
          onShowSnackbar({
            message: 'User not found in organization',
            severity: 'error',
          });
        },
        badRequest: (err) => {
          const errorMessages = Object.values(
            err.user_facing_errors || {}
          ).join(', ');
          onShowSnackbar({
            message: `Error updating role: ${errorMessages}`,
            severity: 'error',
          });
        },
        unexpectedOrUnhandled: (err) => {
          onShowSnackbar({
            message: err.user_facing_error || 'Failed to update role',
            severity: 'error',
          });
        },
      });
    } catch (err) {
      onShowSnackbar({
        message: 'Failed to update role',
        severity: 'error',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  if (isUpdating) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '42px',
        }}
      >
        <Loading
          loading={true}
          containerProps={{height: 'auto', minHeight: 'auto'}}
          loadingProps={{size: '20'}}
        />
      </Box>
    );
  }

  if (orgMember.possible_roles.length === 0 || isCurrentUser) {
    return <Typography variant="body2">{orgMember.role}</Typography>;
  }

  // Create a combined list of roles: current role + possible roles (deduplicated)
  const allRoles = [orgMember.role, ...orgMember.possible_roles];
  const uniqueRoles = [...new Set(allRoles)];

  return (
    <FormControl sx={styles.roleSelect}>
      <Select
        value={orgMember.role}
        onChange={(e) => handleRoleChange(e.target.value)}
        size="small"
        disabled={isUpdating}
      >
        {uniqueRoles.map((role) => (
          <MenuItem key={role} value={role}>
            {role}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

RoleSelect.propTypes = {
  orgId: PropTypes.string.isRequired,
  orgMember: PropTypes.shape({
    user_id: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    possible_roles: PropTypes.arrayOf(PropTypes.string).isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
  onRoleUpdate: PropTypes.func.isRequired,
  onShowSnackbar: PropTypes.func.isRequired,
};

// Action menu component
const ActionMenu = ({orgId, orgMember, onMemberRemoved, onShowSnackbar}) => {
  const {removeUserFromOrg} = useAuthFrontendApis();
  const {userClass} = useAuthInfo();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const open = Boolean(anchorEl);

  const isCurrentUser = orgMember.user_id === userClass?.userId;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRemoveUser = async () => {
    const message = isCurrentUser
      ? 'Are you sure you want to leave the organization?'
      : `Are you sure you want to remove ${orgMember.email} from the organization?`;

    if (!window.confirm(message)) {
      handleClose();
      return;
    }

    setIsRemoving(true);
    try {
      const response = await removeUserFromOrg({
        org_id: orgId,
        user_id: orgMember.user_id,
      });

      await response.handle({
        success: () => {
          onMemberRemoved();
          onShowSnackbar({
            message: isCurrentUser
              ? 'You have left the organization'
              : `${orgMember.email} has been removed from the organization`,
            severity: 'success',
          });
        },
        unexpectedOrUnhandled: (err) => {
          onShowSnackbar({
            message:
              err.user_facing_error ||
              'Failed to remove user from organization',
            severity: 'error',
          });
        },
      });
    } catch (err) {
      onShowSnackbar({
        message: 'Failed to remove user from organization',
        severity: 'error',
      });
    } finally {
      setIsRemoving(false);
      handleClose();
    }
  };

  // Don't show menu if user can't be deleted, if it's the current user, or if currently removing
  if (!orgMember.can_be_deleted || isCurrentUser) {
    return null;
  }

  if (isRemoving) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px',
        }}
      >
        <Loading
          loading={true}
          containerProps={{height: 'auto', minHeight: 'auto'}}
          loadingProps={{size: '16'}}
        />
      </Box>
    );
  }

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{
          color: '#162523',
          '&:hover': {
            backgroundColor: '#F2F4F4',
            color: '#162523',
          },
        }}
      >
        <MoreHorizontal size={20} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            border: '1px solid #E1E5E4',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '120px',
          },
        }}
      >
        <MenuItem
          onClick={handleRemoveUser}
          sx={{
            color: '#C71212',
            fontSize: '14px',
            fontWeight: 500,
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            '&:hover': {
              backgroundColor: '#FEF2F2',
            },
          }}
        >
          <Trash2 size={16} />
          Remove
        </MenuItem>
      </Menu>
    </>
  );
};

ActionMenu.propTypes = {
  orgId: PropTypes.string.isRequired,
  orgMember: PropTypes.shape({
    user_id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    can_be_deleted: PropTypes.bool.isRequired,
  }).isRequired,
  onMemberRemoved: PropTypes.func.isRequired,
  onShowSnackbar: PropTypes.func.isRequired,
};

const CompanyMembers = React.forwardRef(({company}, ref) => {
  const {userClass} = useAuthInfo();
  const {fetchPendingOrgInvites} = useAuthFrontendApis();
  const {isLoading, error, data, fetchOrgMembers} = useOrgMembers(
    company.orgId
  );

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasPendingInvites, setHasPendingInvites] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const pageSize = 25;

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to first page when searching
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // Check for pending invites
  useEffect(() => {
    const checkPendingInvites = async () => {
      try {
        const response = await fetchPendingOrgInvites(company.orgId, {
          page_number: 0,
          page_size: 1,
          email_search: '',
        });

        await response.handle({
          success: (responseData) => {
            setHasPendingInvites(responseData.total_count > 0);
          },
          orgNotFound: () => {
            setHasPendingInvites(false);
          },
          orgsNotEnabled: () => {
            setHasPendingInvites(false);
          },
          unauthorized: () => {
            setHasPendingInvites(false);
          },
          unexpectedOrUnhandled: () => {
            setHasPendingInvites(false);
          },
        });
      } catch (err) {
        setHasPendingInvites(false);
      }
    };

    checkPendingInvites();
  }, [company.orgId, fetchPendingOrgInvites]);

  // Fetch members when page or search changes
  useEffect(() => {
    fetchOrgMembers(page - 1, pageSize, debouncedSearch);
  }, [page, debouncedSearch, fetchOrgMembers]);

  const handleRoleUpdate = useCallback(() => {
    fetchOrgMembers(page - 1, pageSize, debouncedSearch);
  }, [page, debouncedSearch, fetchOrgMembers]);

  const handleMemberRemoved = useCallback(() => {
    fetchOrgMembers(page - 1, pageSize, debouncedSearch);
  }, [page, debouncedSearch, fetchOrgMembers]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleShowSnackbar = useCallback(({message, severity}) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  const refreshPendingInvites = useCallback(async () => {
    try {
      const response = await fetchPendingOrgInvites(company.orgId, {
        page_number: 0,
        page_size: 1,
        email_search: '',
      });

      await response.handle({
        success: (responseData) => {
          setHasPendingInvites(responseData.total_count > 0);
          // Increment refreshTrigger to force CompanyPendingInvitations to refetch
          setRefreshTrigger((prev) => prev + 1);
        },
        orgNotFound: () => {
          setHasPendingInvites(false);
        },
        orgsNotEnabled: () => {
          setHasPendingInvites(false);
        },
        unauthorized: () => {
          setHasPendingInvites(false);
        },
        unexpectedOrUnhandled: () => {
          setHasPendingInvites(false);
        },
      });
    } catch (err) {
      setHasPendingInvites(false);
    }
  }, [company.orgId, fetchPendingOrgInvites]);

  // Refresh members list
  const refreshMembers = useCallback(() => {
    fetchOrgMembers(page - 1, pageSize, debouncedSearch);
  }, [page, debouncedSearch, fetchOrgMembers]);

  // Expose refresh function to parent via ref
  React.useImperativeHandle(
    ref,
    () => ({
      refreshPendingInvites,
      refreshMembers,
    }),
    [refreshPendingInvites, refreshMembers]
  );

  const totalPages = data ? Math.ceil(data.total_count / pageSize) : 0;

  return (
    <Box sx={{paddingBottom: '80px'}}>
      {/* Search and Member Count */}
      <Box
        sx={{
          ...styles.searchContainer,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={styles.searchField}>
          <img src={SearchGreyIcon} height={20} width={20} alt="search" />
          <Box
            component="input"
            type="text"
            placeholder="Search users"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={styles.searchInput}
          />
          {search && (
            <CloseIcon
              sx={{
                fontSize: 16,
                color: '#9AA7A6',
                cursor: 'pointer',
                '&:hover': {
                  color: '#162523',
                },
              }}
              onClick={() => setSearch('')}
            />
          )}
        </Box>

        {/* Member Count */}
        {!isLoading && !error && data && (
          <Typography
            sx={{
              fontSize: '14px',
              color: '#819190',
              fontWeight: 500,
              transform: 'translate(-6px, 6px)',
            }}
          >
            {data.total_count} {data.total_count === 1 ? 'member' : 'members'}
          </Typography>
        )}
      </Box>

      {/* Pending Invitations - Only show if there are pending invites */}
      {hasPendingInvites && (
        <CompanyPendingInvitations
          company={company}
          debouncedSearch={debouncedSearch}
          onShowSnackbar={handleShowSnackbar}
          onInviteRevoked={refreshPendingInvites}
          refreshTrigger={refreshTrigger}
        />
      )}

      {/* Users Table */}
      <SettingsCard>
        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{marginBottom: '16px'}}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && (
          <Box sx={{width: '100%', display: 'flex', justifyContent: 'center'}}>
            <Loading
              loading={true}
              message="Loading users..."
              containerProps={{
                height: '300px',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 'none',
              }}
            />
          </Box>
        )}

        {/* Members Table */}
        {!isLoading && !error && (
          <>
            <TableContainer sx={styles.tableContainer}>
              <Table>
                <TableHead sx={styles.tableHeader}>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell
                      sx={{width: '48px', padding: '16px 8px'}}
                    ></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.users?.length === 0 ? (
                    <TableRow sx={styles.tableRow}>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No company members found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data?.users?.map((member) => (
                      <TableRow key={member.user_id} sx={styles.tableRow}>
                        <TableCell>
                          <Box sx={styles.userCell}>
                            <Typography sx={styles.userName}>
                              {member.email.split('@')[0]}
                              {member.user_id === userClass?.userId
                                ? ' (You)'
                                : ''}
                            </Typography>
                            <Typography sx={styles.userEmail}>
                              {member.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <RoleSelect
                            orgId={company.orgId}
                            orgMember={member}
                            onRoleUpdate={handleRoleUpdate}
                            onShowSnackbar={handleShowSnackbar}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography sx={styles.statusText}>
                            {member.is_enabled ? 'Active' : 'Inactive'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{width: '48px', padding: '16px 8px'}}>
                          <ActionMenu
                            orgId={company.orgId}
                            orgMember={member}
                            onMemberRemoved={handleMemberRemoved}
                            onShowSnackbar={handleShowSnackbar}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={styles.paginationContainer}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </SettingsCard>

      {/* Snackbar for success and error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({...prev, open: false}))}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
        style={{marginTop: '35px'}}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({...prev, open: false}))}
          severity={snackbar.severity}
          icon={
            snackbar.severity === 'success' ? (
              <Box
                sx={{
                  backgroundColor: '#26A993',
                  borderRadius: '50%',
                  width: 22,
                  height: 22,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CheckIcon sx={{color: 'white', fontSize: 16}} />
              </Box>
            ) : (
              <ErrorOutlineIcon sx={{color: '#D73E3E', fontSize: 22}} />
            )
          }
          sx={{
            width: '100%',
            backgroundColor: '#FFFFFF',
            color: '#000000',
            border: '1px solid #E1E5E4',
            borderRadius: '8px',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

CompanyMembers.displayName = 'CompanyMembers';

CompanyMembers.propTypes = {
  company: PropTypes.shape({
    orgId: PropTypes.string.isRequired,
    orgName: PropTypes.string.isRequired,
  }).isRequired,
};

export default CompanyMembers;
